import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import styles from './modal.module.css';

interface ModalProps {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
}

/**
 * Built on the native `<dialog>` element, which brings focus trapping, Escape
 * handling and the top layer for free — all of which a hand-rolled overlay
 * gets subtly wrong.
 */
export function Modal({ title, isOpen, onClose, footer, children }: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen && !dialog.open) dialog.showModal();
    if (!isOpen && dialog.open) dialog.close();
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      /*
       * The backdrop deliberately does nothing.
       *
       * Every dialog here is a form somebody is halfway through filling in, and
       * a click landing next to it — reaching for a field, missing the edge —
       * used to throw that work away with no undo. Closing is left to the three
       * things the user did on purpose: Zapisz, Anuluj and ×.
       */
    >
      <div className={styles.panel}>
        <header className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Zamknij">
            ×
          </button>
        </header>

        <div className={styles.body}>{children}</div>

        {footer && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </dialog>
  );
}

export { styles as modalStyles };
